import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, ChevronDown, Upload, MapPin } from "lucide-react";
import config from "../../config";
import Navbar from "../../components/public/Navbar";
import {
  authHeaders,
  readUser,
  formatDisplayEmail,
  formatDisplayName,
  persistCustomerSession,
  isCustomerLoggedIn,
  CustomerShell,
} from "./customerAccount";
import {
  readBookingCart,
  writeBookingCart,
  clearBookingCart,
  setAuthNext,
  expandCartLines,
  mergeBookingCart,
  readWizardDraft,
  writeWizardDraft,
} from "../../lib/bookingCart";
import { fieldError, indianMobile10, isValidPersonName } from "../../lib/validation";
import { extractPincode, normalizePincode, pinServiceError, PIN_REQUIRED } from "../../lib/servicePincodes";
import { CONSENT_TEXT, CONSENT_VERSION } from "../../lib/consentCopy";
import { useSiteContent } from "../../lib/siteContent";
import { formatDateDMY, parseMapsPin, mapsUrlFor, parseDateValue, ageFromDob, FAMILY_RELATIONS } from "../../lib/dates";
import DmyDateInput from "../../components/DmyDateInput";
import OtpActions from "../../components/auth/OtpActions";
import { InlineLoader } from "../../components/RequestProgress";
import { BookingSlotCalendar, localIsoDate, addDaysIso } from "./BookingWizard";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function formatAddress(a) {
  if (!a) return "";
  if (a.house_no || a.apartment || a.area) {
    return [a.house_no, a.apartment, a.area, a.city, a.landmark, a.pincode].filter(Boolean).join(", ");
  }
  return [a.line1, a.line2, a.city, a.pincode, a.landmark].filter(Boolean).join(", ");
}

function detailMessage(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === "string" ? x : x.msg || JSON.stringify(x))).join(", ");
  }
  if (typeof d === "object" && d.message) return d.message;
  return "Something went wrong";
}

const GST_PERCENT = 18;

function linesFromCharges(charges, fallbackBase = 0, serviceName = "Service") {
  const rows = Array.isArray(charges) ? charges : [];
  const out = [];
  let taxable = 0;
  let nontax = 0;
  rows.forEach((c, j) => {
    if ((c.calc || "fixed") !== "fixed") return;
    const amt = Number(c.amount) || 0;
    if (amt <= 0 && (c.code || "") !== "base") return;
    const isTaxable = c.taxable !== false;
    out.push({
      id: c.id || c.code || `fix-${j}`,
      code: c.code || "custom",
      label: (c.code === "base" ? (c.label || serviceName) : (c.label || "Charge")),
      calc: "fixed",
      amount: amt,
      taxable: isTaxable,
      removable: Boolean(c.removable || c.global || c.auto_apply),
      global: Boolean(c.global || c.auto_apply),
    });
    if (isTaxable) taxable += amt;
    else nontax += amt;
  });
  if (!out.length && fallbackBase) {
    out.push({ id: "base", code: "base", label: serviceName, calc: "fixed", amount: fallbackBase, taxable: true });
    taxable = fallbackBase;
  }
  let gstPercent = 0;
  rows.forEach((c, j) => {
    if ((c.calc || "fixed") !== "percent") return;
    const rate = Number(c.amount) || 0;
    if (rate <= 0) return;
    gstPercent += rate;
    const amt = Math.round(taxable * rate) / 100;
    out.push({
      id: c.id || c.code || `pct-${j}`,
      code: c.code || "tax",
      label: c.label || `Tax (${rate}%)`,
      calc: "percent",
      amount: amt,
      rate,
    });
  });
  const subtotal = Math.round((taxable + nontax) * 100) / 100;
  const total = Math.round(out.reduce((s, l) => s + (Number(l.amount) || 0), 0) * 100) / 100;
  return { lines: out, subtotal, total, gst_percent: gstPercent || GST_PERCENT };
}

function buildPricingFromService(service) {
  if (!service) return null;
  const base = Number(service.indicative_price ?? service.price ?? 0) || 0;
  const gstAmt = Math.round(base * GST_PERCENT) / 100;
  return {
    service_id: service.id,
    service_name: service.name,
    lines: [
      { id: "base", code: "base", label: service.name || "Service", calc: "fixed", amount: base },
      { id: "gst", code: "gst", label: `GST (${GST_PERCENT}%)`, calc: "percent", amount: gstAmt },
    ],
    subtotal: base,
    total: Math.round((base + gstAmt) * 100) / 100,
    gst_percent: GST_PERCENT,
  };
}

function WizardNav({ step, onBack, canNext, onNext, nextLabel = "Next", onAttempt }) {
  const handleNext = () => {
    if (!canNext) {
      if (onAttempt) onAttempt();
      return;
    }
    onNext();
  };
  return (
    <div className="flex gap-3 pt-2">
      {step > 1 ? (
        <button
          type="button"
          onClick={onBack}
          className="flex-1 inline-flex items-center justify-center gap-2 border border-[#e5e7eb] text-[#311b52] rounded-full py-3 font-semibold"
          style={{ fontFamily: "Montserrat" }}
          data-testid="booking-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      ) : null}
      <button
        type="button"
        disabled={String(nextLabel).startsWith("Processing")}
        onClick={handleNext}
        className={`${step > 1 ? "flex-1" : "w-full"} ${
          canNext
            ? "bg-[#f37721] text-white"
            : "bg-[#f37721]/60 text-white cursor-not-allowed"
        } rounded-full py-3 font-semibold transition-all`}
        style={{ fontFamily: "Montserrat" }}
        data-testid={String(nextLabel).startsWith("Processing") || nextLabel === "Confirm booking" || nextLabel === "Pay now" ? "booking-confirm-btn" : "booking-next-btn"}
      >
        {nextLabel}
      </button>
    </div>
  );
}

function AddressPicker({
  addresses,
  value,
  onChange,
  onAddAddress,
  showAddressForm,
  addrForm,
  setAddrForm,
  saveAddress,
  savingExtra,
  testPrefix,
  servicePins = [],
  pinsLoaded = true,
}) {
  const [open, setOpen] = useState(false);
  const selected = addresses.find((a) => a.id === value.address_id);
  const label = selected?.label || (value.address_text ? "Selected address" : "Select address");
  const subtitle = selected ? formatAddress(selected) : (value.address_text || "Choose a saved address");

  return (
    <div className="space-y-2">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testPrefix}-address-select`}
          className={`w-full flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left bg-white transition-colors ${
            open ? "border-[#673c96]" : "border-[#d1d5db] hover:border-[#673c96]"
          }`}
        >
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-[#311b52]">{label}</span>
            <span className="block text-xs text-[#6b7280] mt-0.5 truncate">{subtitle}</span>
          </span>
          <ChevronDown className={`w-5 h-5 shrink-0 text-[#673c96] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-[#e5e7eb] rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {addresses.length === 0 && (
              <p className="px-4 py-3 text-sm text-[#6b7280]">No saved addresses yet.</p>
            )}
            {addresses.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => {
                  onChange({
                    address_id: a.id,
                    address_text: formatAddress(a),
                    pincode: extractPincode(a.pincode, formatAddress(a)),
                  });
                  setOpen(false);
                }}
                className={`w-full text-left px-4 py-3 border-b border-[#f3f4f6] last:border-b-0 ${
                  value.address_id === a.id ? "bg-[#f37721]/10" : "hover:bg-[#fafafa]"
                }`}
              >
                <span className="block text-sm font-semibold text-[#311b52]">{a.label || "Address"}</span>
                <span className="block text-xs text-[#6b7280] mt-0.5">{formatAddress(a)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => { setOpen(false); onAddAddress(); }}
        className="text-xs font-semibold text-[#673c96]"
        data-testid={`${testPrefix}-add-address`}
      >
        {showAddressForm ? "Hide house, society and area fields" : "+ Add a new address"}
      </button>
      {showAddressForm && (
        <form onSubmit={saveAddress} className="space-y-2 bg-[#fafafa] rounded-xl p-3">
          <input value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} placeholder="House / street *" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input value={addrForm.line2} onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })} placeholder="Apartment / society" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input value={addrForm.line3} onChange={(e) => setAddrForm({ ...addrForm, line3: e.target.value })} placeholder="Area / landmark" className="w-full rounded-lg border px-3 py-2 text-sm" />
          <input
            value={addrForm.pincode || ""}
            onChange={(e) => setAddrForm({ ...addrForm, pincode: normalizePincode(e.target.value) })}
            placeholder="6-digit PIN code *"
            maxLength={6}
            inputMode="numeric"
            className={`w-full rounded-lg border px-3 py-2 text-sm ${
              normalizePincode(addrForm.pincode).length === 6 && pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded })
                ? "border-red-500 bg-red-50"
                : ""
            }`}
          />
          {normalizePincode(addrForm.pincode).length === 6 && pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded }) ? (
            <p className="text-xs text-red-600">{pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded })}</p>
          ) : null}
          <button type="submit" disabled={savingExtra} className="bg-[#673c96] text-white rounded-full px-4 py-2 text-sm font-semibold">Save address</button>
        </form>
      )}
    </div>
  );
}

function Chip({ on, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
        on ? "bg-[#f37721] text-white border-[#f37721]" : "border-[#e5e7eb] text-[#311b52] bg-white"
      }`}
    >
      {children}
    </button>
  );
}

function rxReady(line = {}) {
  if (line.prescription_status === "not_required") return true;
  if (line.prescription_status === "have" && (line.prescription_file_reference || line.prescription_file || line.prescription_file_name)) return true;
  return false;
}

function rxSummary(line = {}) {
  if (line.prescription_status === "have" && (line.prescription_file_name || line.prescription_file_reference)) {
    return line.prescription_file_name ? `Rx · ${line.prescription_file_name}` : "Prescription uploaded";
  }
  if (line.prescription_status === "have") return "Upload prescription";
  if (line.prescription_status === "not_required") return "Doctor will call";
  return "Prescription needed";
}

function PrescriptionPicker({ value, onChange, loggedIn, onError, testPrefix }) {
  const [uploading, setUploading] = useState(false);
  const pickHave = () => onChange({ prescription_status: "have" });
  const pickWithout = () => onChange({
    prescription_status: "not_required",
    prescription_file_reference: "",
    prescription_file_name: "",
    prescription_file: null,
  });
  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      onError?.("Prescription must be under 10 MB");
      return;
    }
    if (!loggedIn) {
      onChange({
        prescription_status: "have",
        prescription_file: file,
        prescription_file_name: file.name,
        prescription_file_reference: "",
      });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("prescription", file);
      const { data } = await axios.post(`${config.API}/customer/prescriptions/upload`, fd, { headers: authHeaders() });
      onChange({
        prescription_status: "have",
        prescription_file_reference: data.prescription_file_reference,
        prescription_file_name: file.name,
        prescription_file: null,
      });
    } catch (err) {
      onError?.(detailMessage(err) || "Could not upload prescription");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="text-sm font-semibold text-[#311b52] mb-1">4. Prescription</p>
      <p className="text-xs text-[#6b7280] mb-2">Upload a file if you have one. If not, a doctor will call you and help.</p>
      <div className="flex flex-wrap gap-2">
        <Chip on={value.prescription_status === "have"} onClick={pickHave} testId={`${testPrefix}-rx-have`}>
          Upload
        </Chip>
        <Chip on={value.prescription_status === "not_required"} onClick={pickWithout} testId={`${testPrefix}-rx-without`}>
          Proceed with Doctor's consultation
        </Chip>
      </div>
      {value.prescription_status === "have" && (
        <label className="mt-2 flex items-center gap-3 cursor-pointer rounded-xl border-2 border-dashed border-[#673c96]/30 bg-[#fbf8f2] px-4 py-3 hover:border-[#f37721] transition">
          <Upload className="w-4 h-4 text-[#f37721] shrink-0" />
          <span className="text-sm text-[#311b52]">
            {uploading ? "Uploading…" : (value.prescription_file_name || "Choose file (PDF, JPG, PNG, HEIC)")}
          </span>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
            className="hidden"
            data-testid={`${testPrefix}-rx-upload`}
            disabled={uploading}
            onChange={(e) => onFile(e.target.files?.[0] || null)}
          />
        </label>
      )}
    </div>
  );
}

function ageReady(line) {
  const fromDob = ageFromDob(line?.beneficiary_dob);
  if (fromDob !== "") return true;
  if (line?.beneficiary_age === "" || line?.beneficiary_age == null) return false;
  const n = Number(line.beneficiary_age);
  return Number.isFinite(n) && n >= 0 && n <= 120;
}

function applyDob(dob, extra = {}) {
  const age = ageFromDob(dob);
  return { beneficiary_dob: dob, beneficiary_age: age === "" ? "" : age, ...extra };
}

function LocationPin({ value, onChange, testPrefix }) {
  const hasPin = value.geo_lat != null && value.geo_lng != null;
  const [open, setOpen] = useState(false);
  const [paste, setPaste] = useState(
    value.geo_maps_url || (hasPin ? `${value.geo_lat},${value.geo_lng}` : ""),
  );

  useEffect(() => {
    if (value.geo_lat == null || value.geo_lng == null) return;
    setPaste(value.geo_maps_url || `${value.geo_lat},${value.geo_lng}`);
  }, [value.geo_lat, value.geo_lng, value.geo_maps_url]);

  const apply = (lat, lng, url) => {
    onChange({
      geo_lat: lat,
      geo_lng: lng,
      geo_maps_url: url || mapsUrlFor(lat, lng),
    });
  };

  const useCurrent = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const url = mapsUrlFor(lat, lng);
        setPaste(`${lat},${lng}`);
        apply(lat, lng, url);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
    );
  };

  const onPaste = (text) => {
    setPaste(text);
    const pin = parseMapsPin(text);
    if (pin) apply(pin.lat, pin.lng, /^https?:/i.test(text.trim()) ? text.trim() : mapsUrlFor(pin.lat, pin.lng));
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#673c96]"
        data-testid={`${testPrefix}-geo-open`}
      >
        <MapPin className="w-3.5 h-3.5" />
        {hasPin ? "Map pin saved · Change" : "Add a map pin (optional)"}
      </button>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold text-[#311b52]">Map pin</p>
        <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-[#6b7280]">Hide</button>
      </div>
      <p className="text-xs text-[#6b7280] mb-2">Helps the nurse find this address.</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={useCurrent} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#673c96] text-[#673c96] text-xs font-semibold" data-testid={`${testPrefix}-geo-current`}>
          <MapPin className="w-3.5 h-3.5" /> Use my location
        </button>
        {hasPin && value.geo_maps_url && (
          <a href={value.geo_maps_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#f37721]">
            View pin
          </a>
        )}
      </div>
      <input
        value={paste}
        onChange={(e) => onPaste(e.target.value)}
        placeholder="Paste a Google Maps link"
        className="mt-2 w-full rounded-xl border border-[#e5e7eb] px-4 py-2.5 text-sm"
        data-testid={`${testPrefix}-geo-paste`}
      />
      {hasPin && (
        <p className="text-[11px] text-emerald-700 mt-1">Pin saved</p>
      )}
    </div>
  );
}

function DetailsEditor({
  value,
  onChange,
  loggedIn,
  selfName,
  family,
  addresses,
  onSelectSelf,
  onAddFamily,
  onAddAddress,
  showAddressForm,
  addrForm,
  setAddrForm,
  saveAddress,
  savingExtra,
  calendarDays,
  calendarLoading,
  calendarTotal,
  slotsByDate,
  slotsLoading,
  takenHours,
  testPrefix,
  onError,
  attempted,
  servicePins,
  pinsLoaded,
  showUseFirstAddress,
  onUseFirstAddress,
  previousSources,
  onCopyPatient,
  onCopyAddress,
}) {
  const [touched, setTouched] = useState({});
  const mark = (field) => setTouched((t) => (t[field] ? t : { ...t, [field]: true }));
  const nonSelfFamily = family.filter((m) => String(m.relation || "").toLowerCase() !== "self");
  const taken = new Set(takenHours || []);
  const openHours = (slotsByDate[value.preferred_date] || []).filter((s) => {
    const key = `${value.preferred_date}|${s.hour}`;
    const takenByOther = taken.has(key);
    if (takenByOther && value.preferred_slot !== s.hour) return false;
    return s.status === "available" || value.preferred_slot === s.hour;
  });

  const pinDigits = normalizePincode(value.pincode);
  const showPinRequired = attempted || touched.pincode;
  const pinErr = pinDigits.length === 6
    ? pinServiceError(pinDigits, servicePins, { listReady: pinsLoaded, requireComplete: true })
    : (showPinRequired ? PIN_REQUIRED : "");
  const nameMissing = (attempted || touched.name) && !isValidPersonName(value.beneficiary_name);
  const ageMissing = (attempted || touched.dob) && !ageReady(value);
  const addressMissing = (attempted || touched.address) && !(value.address_text || "").trim();
  const dateMissing = (attempted || touched.date) && !value.preferred_date;
  const slotMissing = Boolean(value.preferred_date) && !value.preferred_slot && (attempted || touched.date || touched.slot);

  return (
    <div className="space-y-6">
      <div>
        <p className={`text-sm font-semibold mb-2 ${nameMissing || ageMissing ? "text-red-600" : "text-[#311b52]"}`}>1. Patient name and date of birth {(nameMissing || ageMissing) && <span className="font-normal text-xs">— required</span>}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <input
            value={value.beneficiary_name}
            onChange={(e) => onChange({ beneficiary_name: e.target.value })}
            onBlur={() => mark("name")}
            placeholder="Full name"
            className={`sm:col-span-2 w-full rounded-xl border px-4 py-2.5 text-sm transition-colors ${nameMissing ? "border-red-500 bg-red-50 focus:ring-red-300" : "border-[#e5e7eb]"}`}
            data-testid={`${testPrefix}-beneficiary-name`}
          />
          <div>
            <DmyDateInput
              required
              noFuture
              max={localIsoDate()}
              value={value.beneficiary_dob || ""}
              onChange={(iso) => { onChange(applyDob(iso)); mark("dob"); }}
              className={`w-full rounded-xl border px-4 py-2.5 text-sm transition-colors ${ageMissing ? "border-red-500 bg-red-50" : "border-[#e5e7eb]"}`}
              data-testid={`${testPrefix}-beneficiary-dob`}
            />
            <p className="text-[11px] text-[#6b7280] mt-1" data-testid={`${testPrefix}-beneficiary-age`}>
              {ageReady(value) ? `Age: ${value.beneficiary_age} years` : "Age appears after date of birth"}
            </p>
          </div>
        </div>
        {nameMissing && <p className="text-xs text-red-600 mt-1">Please enter the patient's full name.</p>}
        {ageMissing && <p className="text-xs text-red-600 mt-1">Please enter the patient's date of birth.</p>}
        {(previousSources || []).some((s) => s.beneficiary_name || s.beneficiary_dob) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {(previousSources || []).filter((s) => s.beneficiary_name || s.beneficiary_dob).map((s) => (
              <button
                key={`copy-patient-${s.key}`}
                type="button"
                onClick={() => onCopyPatient?.(s)}
                className="text-xs font-semibold text-[#673c96] underline"
                data-testid={`${testPrefix}-copy-patient-${s.qtyIndex || s.key}`}
              >
                Copy name & DOB from {s.beneficiary_name || s.name || "appointment above"}
              </button>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {loggedIn && (
            <Chip
              on={value.beneficiary_id === "self"}
              onClick={onSelectSelf}
              testId={`${testPrefix}-beneficiary-self`}
            >
              Me{selfName && selfName !== "Your account" ? ` · ${selfName}` : ""}
            </Chip>
          )}
          {nonSelfFamily.map((m) => (
            <Chip
              key={m.id}
              on={value.beneficiary_id === m.id}
              onClick={() => onChange({
                beneficiary_id: m.id,
                beneficiary_name: m.full_name,
                relation: m.relation || "",
                ...applyDob(m.date_of_birth || ""),
              })}
              testId={`${testPrefix}-beneficiary-${m.id}`}
            >
              {m.full_name}
            </Chip>
          ))}
          <button type="button" onClick={onAddFamily} className="inline-flex items-center gap-1 text-xs font-semibold text-[#f37721]">
            <Plus className="w-3.5 h-3.5" /> Add family
          </button>
        </div>
      </div>

      <div>
        <p className={`text-sm font-semibold mb-2 ${addressMissing || pinErr ? "text-red-600" : "text-[#311b52]"}`}>2. Visit address {(addressMissing || pinErr) && <span className="font-normal text-xs">— required</span>}</p>
        {((previousSources || []).some((s) => (s.address_text || "").trim()) || showUseFirstAddress) && (
          <div className="flex flex-wrap gap-2 mb-2">
            {(previousSources || []).filter((s) => (s.address_text || "").trim()).map((s) => (
              <button
                key={`copy-addr-${s.key}`}
                type="button"
                onClick={() => onCopyAddress?.(s)}
                className="text-xs font-semibold text-[#673c96] underline"
                data-testid={`${testPrefix}-copy-address-${s.key}`}
              >
                Copy address from {s.beneficiary_name || s.name || "appointment above"}
              </button>
            ))}
            {showUseFirstAddress && !(previousSources || []).length && (
              <button type="button" onClick={onUseFirstAddress} className="text-xs font-semibold text-[#673c96]" data-testid={`${testPrefix}-use-first-address`}>
                Use address from the first vaccine
              </button>
            )}
          </div>
        )}
        {loggedIn ? (
          <AddressPicker
            addresses={addresses}
            value={value}
            onChange={(patch) => {
              onChange(patch);
              if (patch.address_text !== undefined || patch.address_id !== undefined) mark("address");
              if (patch.pincode !== undefined) mark("pincode");
            }}
            onAddAddress={onAddAddress}
            showAddressForm={showAddressForm}
            addrForm={addrForm}
            setAddrForm={setAddrForm}
            saveAddress={saveAddress}
            savingExtra={savingExtra}
            testPrefix={testPrefix}
            hasError={addressMissing}
            servicePins={servicePins}
            pinsLoaded={pinsLoaded}
          />
        ) : (
          <>
            <textarea
              value={value.address_text}
              onChange={(e) => onChange({ address_text: e.target.value })}
              onBlur={() => mark("address")}
              placeholder="House, street, area, city"
              className={`w-full rounded-xl border px-4 py-2.5 text-sm transition-colors ${addressMissing ? "border-red-500 bg-red-50" : "border-[#e5e7eb]"}`}
              rows={2}
              data-testid={`${testPrefix}-address-text`}
            />
            <button type="button" onClick={onAddAddress} className="mt-2 text-xs font-semibold text-[#673c96]">
              {showAddressForm ? "Hide house, society and area fields" : "+ Use house, society and area fields"}
            </button>
            {showAddressForm && (
              <form onSubmit={saveAddress} className="space-y-2 bg-[#fafafa] rounded-xl p-3 mt-2">
                <input value={addrForm.line1} onChange={(e) => setAddrForm({ ...addrForm, line1: e.target.value })} placeholder="House / street *" className="w-full rounded-lg border px-3 py-2 text-sm" />
                <input value={addrForm.line2} onChange={(e) => setAddrForm({ ...addrForm, line2: e.target.value })} placeholder="Apartment / society" className="w-full rounded-lg border px-3 py-2 text-sm" />
                <input value={addrForm.line3} onChange={(e) => setAddrForm({ ...addrForm, line3: e.target.value })} placeholder="Area / landmark" className="w-full rounded-lg border px-3 py-2 text-sm" />
                <input
                  value={addrForm.pincode || ""}
                  onChange={(e) => setAddrForm({ ...addrForm, pincode: normalizePincode(e.target.value) })}
                  placeholder="6-digit PIN code *"
                  maxLength={6}
                  inputMode="numeric"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
                {normalizePincode(addrForm.pincode).length === 6 && pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded }) ? (
                  <p className="text-xs text-red-600">{pinServiceError(addrForm.pincode, servicePins, { listReady: pinsLoaded })}</p>
                ) : null}
                <button type="submit" disabled={savingExtra} className="bg-[#673c96] text-white rounded-full px-4 py-2 text-sm font-semibold">Save address</button>
              </form>
            )}
          </>
        )}
        <input
          value={value.pincode || ""}
          onChange={(e) => {
            const pin = normalizePincode(e.target.value);
            onChange({ pincode: pin });
            if (pin.length === 6) mark("pincode");
          }}
          onBlur={() => mark("pincode")}
          placeholder="6-digit PIN code *"
          maxLength={6}
          inputMode="numeric"
          className={`mt-2 w-full rounded-xl border px-4 py-2.5 text-sm transition-colors ${pinErr ? "border-red-500 bg-red-50" : "border-[#e5e7eb]"}`}
          data-testid={`${testPrefix}-pincode`}
        />
        {addressMissing && <p className="text-xs text-red-600 mt-1">Please select or enter a visit address.</p>}
        {pinErr && <p className="text-xs text-red-600 mt-1" data-testid={`${testPrefix}-pincode-error`}>{pinErr}</p>}
        <div className="mt-3">
          <LocationPin value={value} onChange={onChange} testPrefix={testPrefix} />
        </div>
      </div>

      <div>
        <p className={`text-sm font-semibold mb-2 ${dateMissing || slotMissing ? "text-red-600" : "text-[#311b52]"}`}>3. Date and time {(dateMissing || slotMissing) && <span className="font-normal text-xs">— required</span>}</p>
        <div className={dateMissing ? "ring-2 ring-red-400 rounded-xl" : ""}>
          <BookingSlotCalendar
            days={calendarDays}
            selected={value.preferred_date}
            onSelect={(d) => { onChange({ preferred_date: d, preferred_slot: "" }); mark("date"); }}
            loading={calendarLoading}
            totalSlots={calendarTotal}
            compact
          />
        </div>
        {dateMissing && <p className="text-xs text-red-600 mt-1">Please select a date.</p>}
        {value.preferred_date ? (
          <div className="mt-2">
            <p className="text-xs text-[#6b7280] mb-1.5">Pick a time for {formatDateDMY(value.preferred_date)}</p>
            {slotsLoading?.[value.preferred_date] ? (
              <InlineLoader label="Loading time slots…" />
            ) : (
            <div className={`flex flex-wrap gap-2 ${slotMissing ? "p-2 rounded-xl ring-2 ring-red-400" : ""}`}>
              {openHours.length === 0 && <p className="text-sm text-[#6b7280]">No times left on this day{taken.size ? " — this hour may already be used on another vaccine in this booking" : ""}. Pick another date or time.</p>}
              {openHours.map((s) => (
                <Chip
                  key={s.hour}
                  on={value.preferred_slot === s.hour}
                  onClick={() => { onChange({ preferred_slot: s.hour }); mark("slot"); }}
                >
                  {s.label || s.hour}
                </Chip>
              ))}
            </div>
            )}
            {slotMissing && !slotsLoading?.[value.preferred_date] && <p className="text-xs text-red-600 mt-1">Please select a time slot.</p>}
          </div>
        ) : (
          <p className="text-xs text-[#6b7280] mt-2">Times appear after you pick a date.</p>
        )}
      </div>

      <PrescriptionPicker
        value={value}
        onChange={onChange}
        loggedIn={loggedIn}
        onError={onError}
        testPrefix={testPrefix}
      />
    </div>
  );
}

function missingFor(line, duplicateStamps, servicePins = [], pinsLoaded = true) {
  const bits = [];
  if (!isValidPersonName(line.beneficiary_name)) bits.push("name");
  if (!ageReady(line)) bits.push("date of birth");
  if (!(line.address_text || "").trim()) bits.push("address");
  const pinErr = pinServiceError(line.pincode, servicePins, { listReady: pinsLoaded, requireComplete: true });
  if (pinErr) bits.push(pinErr);
  if (!(line.preferred_date && line.preferred_slot)) bits.push("date & time");
  else if (duplicateStamps && duplicateStamps.has(`${line.preferred_date}|${line.preferred_slot}`)) {
    bits.push("unique time (already used on another vaccine)");
  }
  if (!rxReady(line)) bits.push("prescription");
  return bits;
}

function appointmentFacts(line) {
  const bits = [
    line.beneficiary_name ? `${line.beneficiary_name}${ageReady(line) ? `, ${line.beneficiary_age} yrs` : ""}` : "Name needed",
    line.preferred_date && line.preferred_slot ? `${formatDateDMY(line.preferred_date)} · ${line.preferred_slot}` : "Date needed",
    (line.address_text || "").trim() || "Address needed",
    extractPincode(line.pincode) ? `PIN ${extractPincode(line.pincode)}` : "PIN needed",
    rxSummary(line),
  ];
  if (line.geo_lat != null) bits.push("Pin saved");
  return bits;
}

const EMPTY_DETAILS = {
  beneficiary_name: "",
  beneficiary_id: "",
  beneficiary_age: "",
  beneficiary_dob: "",
  relation: "",
  address_text: "",
  address_id: "",
  preferred_date: "",
  preferred_slot: "",
  prescription_status: "",
  prescription_file_reference: "",
  prescription_file_name: "",
  geo_lat: null,
  geo_lng: null,
  geo_maps_url: "",
  pincode: "",
};

function emptyLine(src) {
  return {
    key: src.key,
    service_id: src.service_id,
    name: src.name,
    category: src.category,
    indicative_price: src.indicative_price,
    qtyIndex: src.qtyIndex,
    qtyTotal: src.qtyTotal,
    brand_id: src.brand_id || "",
    brand_name: src.brand_name || "",
    ...EMPTY_DETAILS,
  };
}

function persistCartFromLines(nextLines) {
  const byId = new Map();
  nextLines.forEach((l) => {
    const id = l.brand_id ? `${l.service_id}::${l.brand_id}` : l.service_id;
    const row = byId.get(id);
    if (row) row.qty += 1;
    else {
      byId.set(id, {
        id,
        service_id: l.service_id,
        brand_id: l.brand_id || "",
        brand_name: l.brand_name || "",
        name: l.name,
        category: l.category,
        indicative_price: l.indicative_price,
        qty: 1,
      });
    }
  });
  writeBookingCart([...byId.values()]);
}

function serializeDraftLines(lines) {
  return (lines || []).map((l) => {
    const copy = { ...l };
    delete copy.prescription_file;
    return copy;
  });
}

function restoreLinesFromDraft(expanded, draftLines) {
  const prev = Array.isArray(draftLines) ? draftLines : [];
  const byKey = new Map(prev.map((l) => [l.key, l]));
  return expanded.map((src) => {
    const match = byKey.get(src.key)
      || prev.find((l) => l.service_id === src.service_id && Number(l.qtyIndex) === Number(src.qtyIndex));
    if (!match) return emptyLine(src);
    return {
      ...emptyLine(src),
      ...match,
      key: src.key,
      service_id: src.service_id,
      name: src.name,
      category: src.category,
      indicative_price: src.indicative_price,
      qtyIndex: src.qtyIndex,
      qtyTotal: src.qtyTotal,
    };
  });
}

function remapQtyMeta(nextLines) {
  const counts = {};
  nextLines.forEach((l) => {
    counts[l.service_id] = (counts[l.service_id] || 0) + 1;
  });
  const seen = {};
  return nextLines.map((l) => {
    seen[l.service_id] = (seen[l.service_id] || 0) + 1;
    return { ...l, qtyIndex: seen[l.service_id], qtyTotal: counts[l.service_id] };
  });
}

export function BookingWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { type: pathType, id: pathId } = useParams();
  const [params] = useSearchParams();
  const loggedIn = isCustomerLoggedIn();
  const site = useSiteContent();
  const startedAsGuest = useRef(!loggedIn);
  const user = readUser();
  const selfName = (user.full_name || "").trim() || formatDisplayName(user);

  const [step, setStep] = useState(1);
  const [lines, setLines] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [family, setFamily] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showAddressFormFor, setShowAddressFormFor] = useState("");
  const [famForm, setFamForm] = useState({ full_name: "", relation: "Child", date_of_birth: "", gender: "" });
  const [addrForm, setAddrForm] = useState({ line1: "", line2: "", line3: "", label: "Home", pincode: "" });
  const [savingExtra, setSavingExtra] = useState(false);
  const [familyTarget, setFamilyTarget] = useState("");
  const [calendarDays, setCalendarDays] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarTotal, setCalendarTotal] = useState(0);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [slotsLoading, setSlotsLoading] = useState({});
  const [pricingByService, setPricingByService] = useState({});
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const [doneList, setDoneList] = useState([]);
  const [paying, setPaying] = useState(false);
  const [guestPhone, setGuestPhone] = useState(user.phone ? String(user.phone).slice(-10) : "");
  const [guestName, setGuestName] = useState((user.full_name || "").trim());
  const [guestNameError, setGuestNameError] = useState(false);
  const guestNameRef = useRef(null);
  const errorRef = useRef(null);
  const [guestOtp, setGuestOtp] = useState("");
  const [guestOtpSent, setGuestOtpSent] = useState(false);
  const [guestHint, setGuestHint] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [consentScrolled, setConsentScrolled] = useState(false);
  const [consentPopup, setConsentPopup] = useState("");
  const [minorGuardianConsent, setMinorGuardianConsent] = useState(false);
  const [optOutIds, setOptOutIds] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");
  const [geo, setGeo] = useState({ lat: null, lng: null, accuracy: null, status: "pending" });
  const [leadHours, setLeadHours] = useState(4);
  const [servicePins, setServicePins] = useState([]);
  const [pinsLoaded, setPinsLoaded] = useState(false);
  const consentRef = useRef(null);
  const draftReady = useRef(false);

  const seedLines = () => {
    const deepId = pathId || params.get("service_id") || "";
    const deepName = params.get("name") || location.state?.serviceName || "";
    if (deepId) {
      const existing = readBookingCart().find((s) => s.id === deepId);
      mergeBookingCart({
        id: deepId,
        name: deepName || existing?.name || "Selected service",
        category: params.get("category") || existing?.category || "adult",
        indicative_price: existing?.indicative_price || null,
        qty: existing?.qty || 1,
      });
    }
    const expanded = expandCartLines();
    const draft = readWizardDraft();
    const next = restoreLinesFromDraft(expanded, draft?.lines);
    setLines(next);
    const newIncomplete = next.some((l) => !String(l.beneficiary_name || "").trim() || !l.beneficiary_dob);
    if (draft?.step && !newIncomplete) setStep(Number(draft.step) || 1);
    else setStep(1);
    if (next[0]?.key) setEditingKey(next[0].key);
    draftReady.current = true;
    return next;
  };

  useEffect(() => {
    const expanded = seedLines();
    if (expanded.length === 0) {
      navigate("/vaccination-catalogue", { replace: true });
    }
    axios.get(`${config.API}/config/booking-rules`).then(({ data }) => {
      setLeadHours(Number(data.booking_lead_hours) || 0);
    }).catch(() => {});
    axios.get(`${config.API}/config/service-pincodes`).then(({ data }) => {
      setServicePins(Array.isArray(data.pincodes) ? data.pincodes : []);
    }).catch(() => {
      setServicePins([]);
    }).finally(() => setPinsLoaded(true));
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGeo({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          status: "ok",
        }),
        () => setGeo((g) => ({ ...g, status: "denied" })),
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 },
      );
    } else {
      setGeo((g) => ({ ...g, status: "unavailable" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draftReady.current || !lines.length) return;
    writeWizardDraft({
      lines: serializeDraftLines(lines),
      step,
      consentAccepted,
      consentScrolled,
      minorGuardianConsent,
    });
  }, [lines, step, consentAccepted, consentScrolled, minorGuardianConsent]);

  useEffect(() => {
    if (geo.status !== "ok" || geo.lat == null || geo.lng == null) return;
    const url = mapsUrlFor(geo.lat, geo.lng);
    setLines((prev) => prev.map((l) => (
      l.geo_lat != null ? l : { ...l, geo_lat: geo.lat, geo_lng: geo.lng, geo_maps_url: url }
    )));
  }, [geo.status, geo.lat, geo.lng]);

  const loadFamily = () =>
    axios.get(`${config.API}/customer/family`, { headers: authHeaders() })
      .then(({ data }) => setFamily(data.items || []))
      .catch(() => setFamily([]));

  const loadAddresses = () =>
    axios.get(`${config.API}/customer/addresses`, { headers: authHeaders() })
      .then(({ data }) => {
        const items = data.items || [];
        setAddresses(items);
        setLines((prev) => prev.map((line) => {
          if (line.address_id || line.address_text || items.length === 0) return line;
          const first = items[0];
          return { ...line, address_id: first.id, address_text: formatAddress(first), pincode: extractPincode(first.pincode, formatAddress(first)) || line.pincode };
        }));
      })
      .catch(() => setAddresses([]));

  useEffect(() => {
    if (isCustomerLoggedIn()) {
      loadFamily();
      loadAddresses();
    }
  }, [loggedIn]);

  useEffect(() => {
    const keys = [...new Set(lines.map((l) => `${l.service_id}::${l.brand_id || ""}`).filter((k) => !k.startsWith("::") && !k.startsWith("undefined")))];
    keys.forEach((key) => {
      if (pricingByService[key]) return;
      const [id, brandId] = key.split("::");
      axios.get(`${config.API}/services/${id}/pricing`, { params: brandId ? { brand_id: brandId } : {} })
        .then(({ data }) => setPricingByService((p) => ({ ...p, [key]: data, [id]: data })))
        .catch(() => {
          const line = lines.find((l) => l.service_id === id && (l.brand_id || "") === (brandId || ""));
          setPricingByService((p) => ({
            ...p,
            [key]: buildPricingFromService({
              id,
              name: line?.name,
              indicative_price: line?.indicative_price,
            }),
          }));
        });
    });
  }, [lines, pricingByService]);

  useEffect(() => {
    if (step !== 1) return;
    const start = localIsoDate();
    const end = addDaysIso(start, 45);
    setCalendarLoading(true);
    axios.get(`${config.API}/availability/calendar`, { params: { start, end } })
      .then(({ data }) => {
        const days = data.days || [];
        setCalendarDays(days);
        setCalendarTotal(Number(data.total_slots) || days.reduce((n, d) => n + (d.available_slots || 0), 0));
      })
      .catch(() => { setCalendarDays([]); setCalendarTotal(0); })
      .finally(() => setCalendarLoading(false));
  }, [step]);

  const datesKey = lines.map((l) => l.preferred_date).join("|");
  useEffect(() => {
    const dates = [...new Set(lines.map((l) => l.preferred_date).filter(Boolean))];
    dates.forEach((date) => {
      setSlotsLoading((prev) => ({ ...prev, [date]: true }));
      axios.get(`${config.API}/availability/slots`, { params: { date } })
        .then(({ data }) => setSlotsByDate((prev) => ({ ...prev, [date]: data.slots || [] })))
        .catch(() => setSlotsByDate((prev) => ({ ...prev, [date]: [] })))
        .finally(() => setSlotsLoading((prev) => ({ ...prev, [date]: false })));
    });
  }, [datesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLine = (key, patch) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const selectSelf = (apply) => {
    if (!selfName || selfName === "Your account") {
      setAuthNext("/booking");
      navigate("/profile", { state: { returnTo: "/booking", fromBooking: true } });
      return;
    }
    apply({
      beneficiary_id: "self",
      beneficiary_name: selfName,
      relation: "Self",
      ...applyDob(user.date_of_birth || ""),
    });
  };

  const copyFirstToAll = () => {
    const first = lines[0];
    if (!first) return;
    const details = {};
    Object.keys(EMPTY_DETAILS).forEach((k) => {
      if (k === "preferred_date" || k === "preferred_slot") return;
      details[k] = first[k];
    });
    if (first.prescription_file) details.prescription_file = first.prescription_file;
    setLines((prev) => prev.map((l, i) => (i === 0 ? l : { ...l, ...details })));
  };

  const copyPatientFrom = (src, key) => {
    if (!src) return;
    updateLine(key, {
      beneficiary_id: src.beneficiary_id,
      beneficiary_name: src.beneficiary_name,
      relation: src.relation,
      ...applyDob(src.beneficiary_dob || ""),
    });
  };

  const copyAddressFrom = (src, key) => {
    if (!src) return;
    updateLine(key, {
      address_id: src.address_id,
      address_text: src.address_text,
      pincode: src.pincode,
      geo_lat: src.geo_lat,
      geo_lng: src.geo_lng,
      geo_maps_url: src.geo_maps_url,
    });
  };

  const copyAddressFromFirst = (key) => {
    const first = lines[0];
    if (!first) return;
    copyAddressFrom(first, key);
  };

  const customizeLine = (key, patch) => {
    updateLine(key, patch);
  };

  const removeLine = (key) => {
    const next = remapQtyMeta(lines.filter((l) => l.key !== key));
    persistCartFromLines(next);
    if (next.length === 0) {
      navigate("/vaccination-catalogue");
      return;
    }
    setLines(next);
    setEditingKey(editingKey === key ? (next[0]?.key || null) : editingKey);
    if (familyTarget === key) setFamilyTarget("");
    if (showAddressFormFor === key) setShowAddressFormFor("");
  };

  const saveFamily = async (e) => {
    e.preventDefault();
    setSavingExtra(true);
    setError("");
    try {
      if (!isCustomerLoggedIn()) {
        const err = fieldError({ name: famForm.full_name });
        if (err) { setError(err); return; }
        const id = `local-${Date.now()}`;
        const member = { id, ...famForm };
        setFamily((prev) => [...prev, member]);
        if (familyTarget) {
          customizeLine(familyTarget, {
            beneficiary_id: id,
            beneficiary_name: famForm.full_name,
            relation: famForm.relation,
            ...applyDob(famForm.date_of_birth),
          });
        }
        setFamForm({ full_name: "", relation: "Child", date_of_birth: "", gender: "" });
        setShowFamilyModal(false);
        return;
      }
      const { data } = await axios.post(`${config.API}/customer/family`, famForm, { headers: authHeaders() });
      await loadFamily();
      if (familyTarget) {
        customizeLine(familyTarget, {
          beneficiary_id: data.id,
          beneficiary_name: data.full_name,
          relation: data.relation || famForm.relation,
          ...applyDob(data.date_of_birth || famForm.date_of_birth),
        });
      }
      setFamForm({ full_name: "", relation: "Child", date_of_birth: "", gender: "" });
      setShowFamilyModal(false);
    } catch (err) {
      setError(detailMessage(err) || "Could not add family member");
    } finally {
      setSavingExtra(false);
    }
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    setSavingExtra(true);
    setError("");
    try {
      const text = [addrForm.line1, addrForm.line2, addrForm.line3].filter(Boolean).join(", ");
      if (!text.trim()) { setError("Enter address line 1"); return; }
      const pin = normalizePincode(addrForm.pincode);
      const pinErr = pinServiceError(pin, servicePins, { listReady: pinsLoaded, requireComplete: true });
      if (pinErr) { setError(pinErr); return; }
      if (!isCustomerLoggedIn()) {
        if (showAddressFormFor) customizeLine(showAddressFormFor, { address_id: "", address_text: text, pincode: pin });
        setShowAddressFormFor("");
        return;
      }
      const payload = {
        label: addrForm.label || "Home",
        line1: addrForm.line1.trim(),
        line2: addrForm.line2.trim(),
        line3: addrForm.line3.trim(),
        house_no: addrForm.line1.trim(),
        apartment: addrForm.line2.trim(),
        area: addrForm.line3.trim(),
        city: "",
        landmark: "",
        pincode: pin,
      };
      const { data } = await axios.post(`${config.API}/customer/addresses`, payload, { headers: authHeaders() });
      await loadAddresses();
      if (showAddressFormFor) customizeLine(showAddressFormFor, { address_id: data.id, address_text: formatAddress(data) || text, pincode: extractPincode(data.pincode, pin) });
      setAddrForm({ line1: "", line2: "", line3: "", label: "Home", pincode: "" });
      setShowAddressFormFor("");
    } catch (err) {
      setError(detailMessage(err) || "Could not save address");
    } finally {
      setSavingExtra(false);
    }
  };

  const duplicateStamps = useMemo(() => {
    const counts = {};
    lines.forEach((l) => {
      if (!l.preferred_date || !l.preferred_slot) return;
      const k = `${l.preferred_date}|${l.preferred_slot}`;
      counts[k] = (counts[k] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
  }, [lines]);

  const linesReady = pinsLoaded && lines.length > 0 && lines.every((l) => (
    isValidPersonName(l.beneficiary_name)
    && ageReady(l)
    && (l.address_text || "").trim()
    && !pinServiceError(l.pincode, servicePins, { listReady: pinsLoaded, requireComplete: true })
    && l.preferred_date
    && l.preferred_slot
    && rxReady(l)
  )) && duplicateStamps.size === 0;

  const combinedPricing = useMemo(() => {
    const linesOut = [];
    const groups = [];
    let subtotal = 0;
    let taxAmt = 0;
    let gstPercent = 0;
    lines.forEach((line, i) => {
      const charges = [];
      const pushCharge = (row) => {
        linesOut.push(row);
        charges.push(row);
      };
      const p = pricingByService[`${line.service_id}::${line.brand_id || ""}`] || pricingByService[line.service_id];
      const raw = (p?.charges || []).filter((c) => {
        const id = String(c.id || c.code || "");
        return !optOutIds.includes(id);
      });
      const computed = (p?.lines || []).length
        ? { lines: p.lines.filter((x) => !optOutIds.includes(String(x.id || x.code || ""))) }
        : (raw.length
          ? linesFromCharges(raw, Number(p?.product_price || p?.subtotal || line.indicative_price || 0), line.name)
          : null);
      if (computed && computed.lines.length) {
        computed.lines.forEach((x) => {
          const amt = Number(x.amount) || 0;
          if (amt <= 0) return;
          const isPct = (x.calc || "fixed") === "percent";
          pushCharge({
            ...x,
            id: `${line.key || i}_${x.id || x.code}`,
            sourceId: x.id || x.code,
          });
          if (isPct) {
            taxAmt += amt;
            gstPercent += Number(x.rate) || 0;
          } else {
            subtotal += amt;
          }
        });
      } else {
        const baseAmt = Number(p?.subtotal || line.indicative_price || 0);
        if (baseAmt > 0) {
          pushCharge({
            id: `base_${i}`,
            code: "base",
            label: line.name,
            calc: "fixed",
            amount: baseAmt,
          });
          subtotal += baseAmt;
          const pct = Number(p?.gst_percent) || GST_PERCENT;
          const oneTax = Math.round(baseAmt * pct) / 100;
          pushCharge({ id: `gst_${i}`, code: "gst", label: `GST (${pct}%)`, calc: "percent", amount: oneTax });
          taxAmt += oneTax;
          gstPercent = pct;
        }
      }
      groups.push({
        key: line.key || String(i),
        name: line.name,
        qtyIndex: line.qtyIndex,
        qtyTotal: line.qtyTotal,
        beneficiary_name: line.beneficiary_name,
        beneficiary_age: line.beneficiary_age,
        preferred_date: line.preferred_date,
        preferred_slot: line.preferred_slot,
        address_text: line.address_text,
        rx: rxSummary(line),
        pin: line.geo_lat != null,
        charges,
      });
    });
    const total = Math.round((subtotal + taxAmt) * 100) / 100;
    const discount = coupon ? Math.round(total * Number(coupon.percent) / 100 * 100) / 100 : 0;
    return {
      lines: linesOut,
      groups,
      subtotal: Math.round(subtotal * 100) / 100,
      gst_percent: gstPercent || GST_PERCENT,
      total,
      discount,
      payable: Math.round((total - discount) * 100) / 100,
    };
  }, [lines, pricingByService, coupon, optOutIds]);

  const applyCoupon = async () => {
    setCouponMsg("");
    setCoupon(null);
    if (!couponCode.trim()) return;
    try {
      const { data } = await axios.post(`${config.API}/coupons/validate`, { code: couponCode.trim() });
      setCoupon(data);
      setCouponMsg(`${data.percent}% off applied`);
    } catch (err) {
      setCouponMsg(detailMessage(err) || "Invalid coupon");
    }
  };

  const onConsentScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setConsentScrolled(true);
  };

  const hasMinor = lines.some((l) => Number(l.beneficiary_age) >= 0 && Number(l.beneficiary_age) < 18);
  const PAY_NOT_COMPLETED = "Payment was not completed. No appointment was confirmed and no nurse slot was reserved.";

  const abandonUnpaid = async (groupId) => {
    if (!groupId) return;
    try {
      await axios.post(`${config.API}/customer/bookings/group/${groupId}/abandon-unpaid`, {}, { headers: authHeaders() });
    } catch {
      /* hold expires on the server if this fails */
    }
  };

  const openCheckout = async (booking, order) => {
    if (order.mode === "pay_later") return { payment_status: "pay_later" };
    if (order.mode === "free") return { payment_status: "paid" };
    const payload = {
      booking_id: booking.id,
      booking_group_id: booking.booking_group_id || order.booking_group_id,
    };
    if (order.mock) {
      const { data } = await axios.post(`${config.API}/payments/razorpay/verify`, {
        ...payload,
        razorpay_order_id: order.order_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_signature: "mock",
      }, { headers: authHeaders() });
      return data;
    }
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) {
      throw new Error("Could not open Razorpay. Check your connection and try again.");
    }
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency || "INR",
        name: "BookMyJab",
        description: booking.service_name,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            const { data } = await axios.post(`${config.API}/payments/razorpay/verify`, {
              ...payload,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers: authHeaders() });
            resolve(data);
          } catch (e) {
            reject(e);
          }
        },
        modal: { ondismiss: () => reject(new Error(PAY_NOT_COMPLETED)) },
        prefill: { name: readUser().full_name, email: formatDisplayEmail(readUser()) || undefined, contact: readUser().phone },
        theme: { color: "#f37721" },
      });
      rzp.on("payment.failed", (resp) => {
        const desc = resp?.error?.description || "Payment failed";
        reject(new Error(desc));
      });
      rzp.open();
    });
  };

  const ensureGuestSession = async () => {
    if (isCustomerLoggedIn()) return true;
    if (!isValidPersonName(guestName)) {
      setError("Enter your full name in the billing section.");
      setGuestNameError(true);
      requestAnimationFrame(() => {
        guestNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        guestNameRef.current?.focus();
      });
      return false;
    }
    setGuestNameError(false);
    const err = fieldError({ phone: guestPhone });
    if (err) { setError(err); return false; }
    if (!guestOtpSent) {
      const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(guestPhone) });
      setGuestOtpSent(true);
      setGuestHint(data.message || "OTP sent to your mobile");
      setError("Enter the OTP sent to your mobile to confirm this booking.");
      return false;
    }
    if (!guestOtp.trim()) {
      setError("Enter the 6-digit OTP from SMS.");
      return false;
    }
    const { data } = await axios.post(`${config.API}/auth/otp/verify`, {
      phone: indianMobile10(guestPhone),
      code: guestOtp.trim(),
      role: "customer",
      intent: "booking",
      full_name: guestName.trim(),
    });
    if (data.role && data.role !== "customer") {
      setError("This mobile is registered as staff. Use a customer number to book, or sign in on the staff portal.");
      return false;
    }
    persistCustomerSession(data);
    try {
      await axios.patch(`${config.API}/customer/profile`, { full_name: guestName.trim(), phone: indianMobile10(guestPhone) }, { headers: authHeaders() });
    } catch { /* continue */ }
    return true;
  };

  const persistBookingPatientsAsFamily = async () => {
    let existing = [];
    try {
      const { data } = await axios.get(`${config.API}/customer/family`, { headers: authHeaders() });
      existing = data.items || [];
    } catch {
      existing = [];
    }
    const seen = new Set(existing.map((m) => `${String(m.full_name || "").trim().toLowerCase()}|${String(m.date_of_birth || "").slice(0, 10)}`));
    for (const line of lines) {
      const name = (line.beneficiary_name || "").trim();
      const dob = line.beneficiary_dob || "";
      if (!isValidPersonName(name) || !dob) continue;
      const key = `${name.toLowerCase()}|${dob}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const relation = line.relation || (line.beneficiary_id === "self" ? "Self" : "Other");
      try {
        await axios.post(`${config.API}/customer/family`, {
          full_name: name,
          relation,
          date_of_birth: dob,
        }, { headers: authHeaders() });
      } catch { /* booking should still proceed */ }
    }
  };

  const submit = async () => {
    setError("");
    if (!consentAccepted || !consentScrolled) {
      setError("Please scroll to the end of the terms and accept consent before booking.");
      return;
    }
    if (hasMinor && !minorGuardianConsent) {
      setError("Guardian permission is required for patients under 18.");
      return;
    }
    setPaying(true);
    try {
      const authed = await ensureGuestSession();
      if (!authed) return;
      await persistBookingPatientsAsFamily();
      const stamps = new Set();
      for (const l of lines) {
        const k = `${l.preferred_date}|${l.preferred_slot}`;
        if (stamps.has(k)) {
          setError("Each appointment needs its own date and time. That slot is already used on another vaccine in this booking.");
          return;
        }
        stamps.add(k);
      }
      for (const l of lines) {
        const pinErr = pinServiceError(l.pincode, servicePins, { listReady: pinsLoaded, requireComplete: true });
        if (pinErr) {
          setError(pinErr);
          return;
        }
      }
      const uploadedByFile = {};
      const resolveRxRef = async (line) => {
        if (line.prescription_status !== "have") return "";
        if (line.prescription_file_reference) return line.prescription_file_reference;
        const file = line.prescription_file;
        if (!file) return "";
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (uploadedByFile[key]) return uploadedByFile[key];
        const fd = new FormData();
        fd.append("prescription", file);
        const { data } = await axios.post(`${config.API}/customer/prescriptions/upload`, fd, { headers: authHeaders() });
        uploadedByFile[key] = data.prescription_file_reference;
        return data.prescription_file_reference;
      };
      const itemPayload = [];
      for (let i = 0; i < lines.length; i += 1) {
        const l = lines[i];
        if (!rxReady(l)) {
          setError("Please upload a prescription or choose to proceed without one for each vaccine.");
          return;
        }
        const rxRef = await resolveRxRef(l);
        if (l.prescription_status === "have" && !rxRef) {
          setError("Please upload your prescription file, or choose to proceed without a prescription.");
          return;
        }
        const p = pricingByService[`${l.service_id}::${l.brand_id || ""}`] || pricingByService[l.service_id];
        const total = Number(p?.total ?? l.indicative_price ?? 0);
        itemPayload.push({
          service_type: pathType || params.get("type") || "vaccination",
          service_id: l.service_id,
          brand_id: l.brand_id || "",
          brand_name: l.brand_name || "",
          service_name: l.name,
          vaccine_category: l.category,
          beneficiary_name: l.beneficiary_name,
          beneficiary_age: Number(l.beneficiary_age),
          beneficiary_dob: l.beneficiary_dob,
          relation: l.relation || "",
          beneficiary_id: l.beneficiary_id === "self" || String(l.beneficiary_id).startsWith("local-") ? null : l.beneficiary_id,
          address_id: l.address_id || null,
          address_text: l.address_text,
          pincode: extractPincode(l.pincode, l.address_text),
          preferred_date: l.preferred_date,
          preferred_slot: l.preferred_slot,
          geo_lat: l.geo_lat ?? geo.lat,
          geo_lng: l.geo_lng ?? geo.lng,
          geo_maps_url: l.geo_maps_url || mapsUrlFor(l.geo_lat ?? geo.lat, l.geo_lng ?? geo.lng),
          indicative_price: p?.subtotal ?? l.indicative_price ?? null,
          final_amount: total,
          price_breakdown: p?.lines || combinedPricing.groups[i]?.charges || [],
          prescription_status: l.prescription_status || "not_required",
          prescription_file_reference: rxRef,
          services: [{ id: l.service_id, name: l.name, category: l.category, indicative_price: l.indicative_price }],
        });
      }
      const payload = {
        consent_accepted: true,
        consent_text: CONSENT_TEXT,
        consent_version: CONSENT_VERSION,
        consent_scrolled_to_end: true,
        minor_guardian_consent: hasMinor ? minorGuardianConsent : false,
        opt_out_global_charges: optOutIds,
        payment_method: "upi",
        coupon_code: coupon?.code || "",
        geo_lat: geo.lat,
        geo_lng: geo.lng,
        geo_accuracy: geo.accuracy,
        items: itemPayload,
      };
      const { data } = await axios.post(`${config.API}/customer/bookings/batch`, payload, { headers: authHeaders() });
      const first = (data.items && data.items[0]) || data;
      const groupId = data.booking_group_id;
      let pay = null;
      try {
        const { data: order } = await axios.post(`${config.API}/payments/razorpay/order`, {
          booking_id: first.id,
          booking_group_id: groupId,
        }, { headers: authHeaders() });
        pay = await openCheckout({ ...first, booking_group_id: groupId, service_name: data.service_name }, order);
      } catch (payErr) {
        await abandonUnpaid(groupId);
        setError(detailMessage(payErr) || PAY_NOT_COMPLETED);
        return;
      }
      const paidOk = pay?.payment_status === "paid" || pay?.payment_status === "paid_offline" || pay?.payment_status === "pay_later";
      if (!paidOk) {
        await abandonUnpaid(groupId);
        setError(PAY_NOT_COMPLETED);
        return;
      }
      clearBookingCart();
      const paidItems = (pay?.items?.length ? pay.items : (data.items || [first])).map((item) => ({
        ...item,
        payment_status: pay?.payment_status || item.payment_status,
        razorpay_payment_id: pay?.razorpay_payment_id || item.razorpay_payment_id,
        status: item.status === "awaiting_payment" ? "confirmed" : item.status,
      }));
      setDoneList(paidItems);
      setDone(paidItems[0]);
    } catch (err) {
      setError(detailMessage(err) || "Booking failed");
    } finally {
      setPaying(false);
    }
  };

  const downloadInvoice = () => {
    const bookings = doneList.length > 0 ? doneList : (done ? [done] : []);
    const rows = combinedPricing.lines || [];
    const total = combinedPricing.payable ?? combinedPricing.total ?? 0;
    const html = `
      <html><head><title>BookMyJab Invoice</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #1a1a2e; }
        h1 { color: #311b52; font-size: 24px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th, td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; font-size: 13px; text-align: left; }
        .total-row td { font-weight: bold; border-top: 2px solid #311b52; }
      </style></head><body>
      <h1>BookMyJab</h1>
      <div>Tax Invoice</div>
      <div>Booking refs: ${bookings.map((b) => b.id).join(", ")}</div>
      <table><thead><tr><th>Description</th><th>Patient</th><th>Slot</th></tr></thead><tbody>
      ${bookings.map((b) => `<tr><td>${b.service_name}</td><td>${b.beneficiary_name}</td><td>${formatDateDMY(b.preferred_date)} ${b.preferred_slot}</td></tr>`).join("")}
      ${rows.map((l) => `<tr><td colspan="2">${l.label}</td><td>₹${Number(l.amount).toFixed(2)}</td></tr>`).join("")}
      ${combinedPricing.discount ? `<tr><td colspan="2">Coupon ${coupon?.code || ""}</td><td>-₹${Number(combinedPricing.discount).toFixed(2)}</td></tr>` : ""}
      <tr class="total-row"><td colspan="2">Total payable</td><td>₹${Number(total).toFixed(2)}</td></tr>
      </tbody></table></body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const wrapBody = (inner) => (
    isCustomerLoggedIn() ? (
      <CustomerShell active="bookings">{inner}</CustomerShell>
    ) : (
      <div className="min-h-screen bg-[#f4f5f7]" data-testid="guest-booking">
        <Navbar />
        <div className="pt-[100px] pb-24 max-w-2xl mx-auto px-4">{inner}</div>
      </div>
    )
  );

  if (done) {
    const list = doneList.length > 0 ? doneList : [done];
    const saveRelation = async (bookingId, relation) => {
      try {
        await axios.patch(`${config.API}/customer/bookings/${bookingId}/relation`, { relation }, { headers: authHeaders() });
        setDoneList((prev) => prev.map((b) => (b.id === bookingId ? { ...b, relation } : b)));
        if (done?.id === bookingId) setDone((d) => ({ ...d, relation }));
      } catch (err) {
        setError(detailMessage(err) || "Could not save relation");
      }
    };
    return wrapBody(
      <div className="bg-white rounded-2xl p-8 shadow-sm text-center max-w-lg mx-auto" data-testid="booking-confirmation">
        <div className="text-4xl text-[#f37721] mb-4">✓</div>
        <h2 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>
          {list.some((d) => d.payment_status === "paid" || d.payment_status === "paid_offline") ? "Payment successful" : "Booking received"}
        </h2>
        <p className="mt-2 text-[#6b7280]">
          {list.some((d) => d.payment_status === "paid" || d.payment_status === "paid_offline")
            ? "Payment is confirmed. Your appointment is now live."
            : "We have captured your consent and will confirm each appointment."}
        </p>
        <div className="mt-4 space-y-2 text-left">
          {list.map((d) => (
            <div key={d.id} className="bg-[#f9f5ff] rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-[#311b52]">{d.service_name}</p>
              <p className="text-xs text-[#6b7280]">{d.beneficiary_name}{d.beneficiary_age != null ? `, ${d.beneficiary_age} yrs` : ""} · {formatDateDMY(d.preferred_date)} · {d.preferred_slot}</p>
              {d.payment_status === "paid" || d.payment_status === "paid_offline" ? (
                <p className="text-[11px] font-semibold text-emerald-700 mt-1">Paid{d.razorpay_payment_id ? ` · ${d.razorpay_payment_id}` : ""}</p>
              ) : (
                <p className="text-[11px] font-semibold text-orange-700 mt-1">Payment pending</p>
              )}
              {startedAsGuest.current && (
                <label className="block mt-2 text-xs text-[#311b52]">
                  Who is {d.beneficiary_name || "this"} to you?
                  <select
                    value={d.relation || ""}
                    onChange={(e) => saveRelation(d.id, e.target.value)}
                    className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 text-sm bg-white"
                    data-testid={`guest-relation-${d.id}`}
                  >
                    <option value="">Select relation</option>
                    {FAMILY_RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
              )}
              <p className="text-xs text-[#9ca3af] mt-1">{d.id}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-3">
          <button type="button" onClick={downloadInvoice} className="w-full border-2 border-[#673c96] text-[#673c96] rounded-full py-3 font-semibold">Download invoice</button>
          <Link to="/dashboard?tab=bookings" className="block text-[#673c96] font-semibold">View my bookings</Link>
        </div>
      </div>
    );
  }

  return wrapBody(
    <>
      <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-black/[0.03] max-w-2xl" data-testid="booking-wizard">
        <div className="flex justify-between text-sm text-[#6b7280] mb-6" style={{ fontFamily: "Montserrat" }}>
          <span>Step {step} of 3</span>
          <Link to="/vaccination-catalogue" className="text-[#673c96] font-medium">Change vaccines</Link>
        </div>
        {error && <p ref={errorRef} className="text-sm text-[#ee4036] mb-4 rounded-xl bg-red-50 border border-red-200 px-3 py-2" data-testid="booking-error">{error}</p>}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Booking details</h2>
              <p className="text-sm text-[#6b7280] mt-1">
                {lines.length === 1
                  ? `Fill this visit. Earliest time is ${leadHours} hours from now.`
                  : `Open one vaccine at a time. Each visit needs its own date and time. You can copy the first visit's patient details onto the rest. Earliest time is ${leadHours} hours from now.`}
              </p>
            </div>
            {lines.length > 1 && (
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-[#311b52]">{lines.length} appointments</p>
                <button
                  type="button"
                  onClick={copyFirstToAll}
                  className="text-xs font-semibold text-[#673c96]"
                  data-testid="booking-copy-first"
                >
                  Use first visit details for all
                </button>
              </div>
            )}
            <div className="space-y-3">
              {lines.map((line, index) => {
                const isOpen = editingKey === line.key || lines.length === 1;
                const missing = missingFor(line, duplicateStamps, servicePins, pinsLoaded);
                const ready = missing.length === 0;
                const facts = appointmentFacts(line);
                return (
                  <div key={line.key} className="rounded-2xl border border-[#e5e7eb] overflow-hidden" data-testid={`booking-line-${index}`}>
                    <button
                      type="button"
                      onClick={() => { setShowAddressFormFor(""); setEditingKey(isOpen && lines.length > 1 ? null : line.key); }}
                      className="w-full text-left px-4 py-3 flex items-start gap-3"
                      data-testid={`booking-appointment-toggle-${index}`}
                    >
                      <span className={`mt-0.5 shrink-0 w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center ${ready ? "bg-emerald-100 text-emerald-700" : "bg-[#673c96]/10 text-[#673c96]"}`}>
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-[#311b52] text-sm">
                            {line.name}
                            {line.qtyTotal > 1 ? <span className="font-medium text-[#6b7280]"> · {line.qtyIndex} of {line.qtyTotal}</span> : null}
                          </span>
                          <span className="flex items-center gap-1 shrink-0">
                            <span className={`text-[11px] font-semibold ${ready ? "text-emerald-700" : "text-[#f37721]"}`}>
                              {ready ? "Ready" : "Needs details"}
                            </span>
                            {lines.length > 1 && <ChevronDown className={`w-4 h-4 text-[#673c96] transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                          </span>
                        </span>
                        {!isOpen && (
                          <span className="block text-xs text-[#6b7280] mt-1">{facts.join(" · ")}</span>
                        )}
                        {!isOpen && !ready && (
                          <span className="block text-[11px] text-[#f37721] mt-0.5">Still needed: {missing.join(", ")}</span>
                        )}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 border-t border-[#f3f4f6]" data-testid={`booking-customise-panel-${index}`}>
                        <div className="flex justify-end mb-2">
                          <button
                            type="button"
                            onClick={() => removeLine(line.key)}
                            className="text-xs font-semibold text-[#ee4036]"
                            data-testid={`booking-remove-${index}`}
                          >
                            Remove this vaccine
                          </button>
                        </div>
                        <DetailsEditor
                          value={line}
                          onChange={(patch) => customizeLine(line.key, patch)}
                          loggedIn={loggedIn}
                          selfName={selfName}
                          family={family}
                          addresses={addresses}
                          onSelectSelf={() => selectSelf((patch) => customizeLine(line.key, patch))}
                          onAddFamily={() => { setFamilyTarget(line.key); setShowFamilyModal(true); }}
                          onAddAddress={() => setShowAddressFormFor((cur) => (cur === line.key ? "" : line.key))}
                          showAddressForm={showAddressFormFor === line.key}
                          addrForm={addrForm}
                          setAddrForm={setAddrForm}
                          saveAddress={saveAddress}
                          savingExtra={savingExtra}
                          calendarDays={calendarDays}
                          calendarLoading={calendarLoading}
                          calendarTotal={calendarTotal}
                          slotsByDate={slotsByDate}
                          slotsLoading={slotsLoading}
                          takenHours={lines
                            .filter((l) => l.key !== line.key && l.preferred_date && l.preferred_slot)
                            .map((l) => `${l.preferred_date}|${l.preferred_slot}`)}
                          testPrefix={lines.length === 1 ? "booking-shared" : `booking-line-${index}`}
                          onError={setError}
                          attempted={attempted}
                          servicePins={servicePins}
                          pinsLoaded={pinsLoaded}
                          showUseFirstAddress={index > 0}
                          onUseFirstAddress={() => copyAddressFromFirst(line.key)}
                          previousSources={lines.slice(0, index).filter((l) => (l.beneficiary_name || "").trim() || (l.address_text || "").trim())}
                          onCopyPatient={(src) => copyPatientFrom(src, line.key)}
                          onCopyAddress={(src) => copyAddressFrom(src, line.key)}
                        />
                        {lines.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const missingNow = missingFor(line, duplicateStamps, servicePins, pinsLoaded);
                              if (missingNow.length) {
                                setAttempted(true);
                                setError(missingNow.find((m) => String(m).includes("not operational")) || "Please complete this appointment before continuing.");
                                return;
                              }
                              const nextLine = lines[index + 1];
                              setShowAddressFormFor("");
                              setError("");
                              setEditingKey(nextLine ? nextLine.key : null);
                            }}
                            className="mt-4 w-full rounded-full border border-[#673c96] text-[#673c96] py-2.5 text-sm font-semibold"
                            data-testid={`booking-next-appointment-${index}`}
                          >
                            {index < lines.length - 1 ? "Save and open next vaccine" : "Save and close"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-[#6b7280]">
              <Link
                to="/vaccination-catalogue"
                className="font-semibold text-[#f37721]"
                onClick={() => writeWizardDraft({
                  lines: serializeDraftLines(lines),
                  step,
                  consentAccepted,
                  consentScrolled,
                  minorGuardianConsent,
                })}
              >
                Add another vaccine
              </Link>
            </p>
            {!linesReady && attempted && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <p className="font-semibold mb-1">Please fill in the required fields above:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  {lines.flatMap((l, i) => missingFor(l, duplicateStamps, servicePins, pinsLoaded).map((m) => (
                    <li key={`${i}-${m}`}>{lines.length > 1 ? `Vaccine ${i + 1}: ` : ""}{m}</li>
                  )))}
                </ul>
              </div>
            )}
            {!linesReady && lines.some((l) => l.preferred_slot) && !lines.every(rxReady) && (
              <p className="text-xs text-[#f37721]">Each visit still needs a prescription choice — upload a file or proceed with a doctor consultation.</p>
            )}
            <WizardNav
              step={1}
              canNext={linesReady}
              onAttempt={() => {
                setAttempted(true);
                const firstIncomplete = lines.find((l) => missingFor(l, duplicateStamps, servicePins, pinsLoaded).length > 0);
                if (firstIncomplete) setEditingKey(firstIncomplete.key);
              }}
              onNext={() => {
                const firstIncomplete = lines.find((l) => missingFor(l, duplicateStamps, servicePins, pinsLoaded).length > 0);
                if (firstIncomplete) {
                  setAttempted(true);
                  setEditingKey(firstIncomplete.key);
                  const pinMsg = missingFor(firstIncomplete, duplicateStamps, servicePins, pinsLoaded).find((m) => String(m).includes("not operational"));
                  if (pinMsg) setError(pinMsg);
                  return;
                }
                setAttempted(false);
                setError("");
                setStep(2);
              }}
              nextLabel="Continue to consent"
            />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Terms and consent</h2>
            <p className="text-sm text-[#6b7280]">
              Scroll to the end, then accept. Links open a popup and return you here when you close it.
            </p>
            <div
              ref={consentRef}
              onScroll={onConsentScroll}
              onClick={(e) => {
                const a = e.target.closest("a");
                if (!a) return;
                const href = (a.getAttribute("href") || "").replace("#", "");
                if (["terms", "privacy", "medical"].includes(href)) {
                  e.preventDefault();
                  setConsentPopup(href);
                }
              }}
              data-testid="consent-scroll"
              className={`h-64 overflow-y-auto rounded-xl border border-[#e5e7eb] p-4 text-xs leading-relaxed text-[#311b52] bg-[#fafafa] [&_a]:text-[#f37721] [&_a]:underline ${site.legal?.declaration_html ? "" : "whitespace-pre-wrap"}`}
              style={{ fontFamily: "Montserrat" }}
              {...(site.legal?.declaration_html
                ? { dangerouslySetInnerHTML: { __html: site.legal.declaration_html } }
                : { children: CONSENT_TEXT })}
            />
            {!consentScrolled && <p className="text-xs text-[#f37721]">Keep scrolling to unlock the accept checkbox.</p>}
            <label className={`flex items-start gap-3 text-sm ${consentScrolled ? "text-[#311b52]" : "text-[#9ca3af]"}`}>
              <input
                type="checkbox"
                checked={consentAccepted}
                disabled={!consentScrolled}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-1 accent-[#f37721]"
                data-testid="booking-consent"
              />
              <span
                className="[&_a]:text-[#f37721] [&_a]:underline"
                onClick={(e) => {
                  const a = e.target.closest("a");
                  if (!a) return;
                  e.preventDefault();
                  const href = (a.getAttribute("href") || "").replace("#", "");
                  setConsentPopup(["terms", "privacy", "medical"].includes(href) ? href : "terms");
                }}
                dangerouslySetInnerHTML={{ __html: site.legal?.checkbox_html || "I have scrolled through and accept the <a href=\"#terms\">Terms &amp; Conditions</a>, <a href=\"#privacy\">Privacy Notice</a> and <a href=\"#medical\">medical declaration</a>." }}
              />
            </label>
            {hasMinor && (
              <label className="flex items-start gap-2 text-sm text-[#311b52]" data-testid="minor-guardian-consent">
                <input
                  type="checkbox"
                  checked={minorGuardianConsent}
                  onChange={(e) => setMinorGuardianConsent(e.target.checked)}
                  className="mt-1 accent-[#f37721]"
                />
                <span>A patient in this booking is under 18. I am the parent or legal guardian and I give specific permission for their vaccination.</span>
              </label>
            )}
            <WizardNav step={2} onBack={() => setStep(1)} canNext={consentAccepted && consentScrolled && (!hasMinor || minorGuardianConsent)} onNext={() => setStep(3)} nextLabel="Continue to billing" />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Billing</h2>
            <div className="space-y-3">
              {(combinedPricing.groups || []).map((g, index) => (
                <div key={g.key} className="rounded-xl border border-[#e5e7eb] overflow-hidden" data-testid={`booking-bill-${index}`}>
                  <div className="px-4 py-3 bg-[#fafafa]">
                    <p className="text-sm font-semibold text-[#311b52]">
                      {index + 1}. {g.name}
                      {g.qtyTotal > 1 ? <span className="font-medium text-[#6b7280]"> · {g.qtyIndex} of {g.qtyTotal}</span> : null}
                    </p>
                    <p className="text-xs text-[#6b7280] mt-1">
                      {[
                        g.beneficiary_name ? `${g.beneficiary_name}${g.beneficiary_age !== "" && g.beneficiary_age != null ? `, ${g.beneficiary_age} yrs` : ""}` : "Patient",
                        g.preferred_date && g.preferred_slot ? `${formatDateDMY(g.preferred_date)} · ${g.preferred_slot}` : "",
                        g.address_text || "",
                        g.rx,
                        g.pin ? "Pin saved" : "",
                      ].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {g.charges.map((l) => (
                    <div key={l.id} className="flex justify-between px-4 py-2 text-sm border-t border-[#f3f4f6]">
                      <span className="text-[#6b7280]">
                        {l.label}
                        {l.global || l.removable ? (
                          <button type="button" className="ml-2 text-[11px] text-[#ee4036] font-semibold"
                            onClick={() => setOptOutIds((prev) => [...new Set([...prev, l.sourceId || String(l.code)])])}>
                            Remove
                          </button>
                        ) : null}
                      </span>
                      <span className="font-medium text-[#311b52]">₹{Number(l.amount).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
              <div className="rounded-xl border border-[#e5e7eb] divide-y">
                {combinedPricing.discount > 0 && (
                  <div className="flex justify-between px-4 py-2 text-sm text-emerald-700">
                    <span>Coupon {coupon.code} ({coupon.percent}%)</span>
                    <span>-₹{combinedPricing.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between px-4 py-3 font-semibold text-[#311b52]">
                  <span>Payable</span>
                  <span>₹{Number(combinedPricing.payable).toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Coupon code"
                className="flex-1 rounded-xl border border-[#e5e7eb] px-4 py-3"
                data-testid="booking-coupon"
              />
              <button type="button" onClick={applyCoupon} className="px-4 rounded-full border border-[#673c96] text-[#673c96] font-semibold text-sm">Apply</button>
            </div>
            {couponMsg && <p className="text-xs text-[#673c96]">{couponMsg}</p>}
            <p className="text-sm text-[#6b7280]">Your appointment is confirmed only after Razorpay reports a successful payment. Closing the payment window will not book a nurse slot.</p>
            {!loggedIn && (
              <div className="space-y-2 rounded-xl bg-[#fafafa] p-4">
                <p className="text-sm font-semibold text-[#311b52]">Your contact details</p>
                <input
                  ref={guestNameRef}
                  value={guestName}
                  onChange={(e) => { setGuestName(e.target.value); setGuestNameError(false); }}
                  placeholder="Full name *"
                  className={`w-full rounded-xl border px-4 py-3 ${guestNameError ? "border-red-500 bg-red-50 ring-2 ring-red-300" : ""}`}
                  data-testid="booking-guest-name"
                />
                {guestNameError && <p className="text-xs text-red-600">Enter the billing full name so we can create your account.</p>}
                <input
                  value={guestPhone}
                  onChange={(e) => {
                    setGuestPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                    if (guestOtpSent) {
                      setGuestOtpSent(false);
                      setGuestOtp("");
                      setGuestHint("");
                    }
                  }}
                  placeholder="10-digit mobile *"
                  className="w-full rounded-xl border px-4 py-3"
                  data-testid="booking-guest-phone"
                  disabled={guestOtpSent}
                />
                {guestOtpSent && (
                  <>
                    <input value={guestOtp} onChange={(e) => setGuestOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit OTP" className="w-full rounded-xl border px-4 py-3" data-testid="booking-guest-otp" />
                    <OtpActions
                      loading={paying}
                      onChangeTarget={() => {
                        setGuestOtpSent(false);
                        setGuestOtp("");
                        setGuestHint("Enter the correct mobile number, then pay again to receive a new OTP.");
                      }}
                      onResend={async () => {
                        const { data } = await axios.post(`${config.API}/auth/otp/send`, { phone: indianMobile10(guestPhone) });
                        setGuestHint(data.message || "OTP sent to your mobile");
                      }}
                    />
                  </>
                )}
                {guestHint && <p className="text-xs text-[#673c96]">{guestHint}</p>}
              </div>
            )}
            <WizardNav
              step={3}
              onBack={() => setStep(2)}
              canNext={!paying}
              onNext={submit}
              nextLabel={paying ? "Processing…" : "Pay now"}
            />
          </div>
        )}
      </div>

      {showFamilyModal && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={saveFamily} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
            <h3 className="font-semibold text-[#311b52]">Add family member</h3>
            <input required value={famForm.full_name} onChange={(e) => setFamForm({ ...famForm, full_name: e.target.value })} placeholder="Full name" className="w-full rounded-xl border px-4 py-3" />
            <select required value={famForm.relation} onChange={(e) => setFamForm({ ...famForm, relation: e.target.value })} className="w-full rounded-xl border px-4 py-3">
              {FAMILY_RELATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div>
              <p className="text-xs text-[#6b7280] mb-1">Date of birth *</p>
              <DmyDateInput required noFuture max={localIsoDate()} value={famForm.date_of_birth} onChange={(iso) => setFamForm({ ...famForm, date_of_birth: iso })} className="w-full rounded-xl border px-4 py-3" />
              {ageFromDob(famForm.date_of_birth) !== "" && (
                <p className="text-[11px] text-[#6b7280] mt-1">Age: {ageFromDob(famForm.date_of_birth)} years</p>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowFamilyModal(false)} className="flex-1 border rounded-full py-2">Cancel</button>
              <button type="submit" disabled={savingExtra} className="flex-1 bg-[#f37721] text-white rounded-full py-2 font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
      {consentPopup && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="booking-terms-popup" onClick={() => setConsentPopup("")}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-semibold text-[#311b52] text-lg" style={{ fontFamily: "Poppins" }}>
                {consentPopup === "privacy" ? "Privacy Notice" : consentPopup === "medical" ? "Medical declaration" : "Terms & Conditions"}
              </h3>
              <button type="button" onClick={() => setConsentPopup("")} className="text-sm font-semibold text-[#673c96]">Close</button>
            </div>
            <div
              onScroll={onConsentScroll}
              className="flex-1 overflow-y-auto rounded-xl border border-[#e5e7eb] p-4 text-xs leading-relaxed text-[#311b52] bg-[#fafafa] [&_a]:text-[#f37721] [&_a]:underline"
              style={{ fontFamily: "Montserrat" }}
              dangerouslySetInnerHTML={{
                __html:
                  (consentPopup === "privacy" ? site.legal?.privacy_html : consentPopup === "medical" ? site.legal?.medical_html : site.legal?.terms_html)
                  || site.legal?.declaration_html
                  || CONSENT_TEXT.replace(/\n/g, "<br/>"),
              }}
            />
            <button type="button" onClick={() => setConsentPopup("")} className="mt-4 bg-[#673c96] text-white rounded-full py-2.5 text-sm font-semibold">
              Back to booking
            </button>
          </div>
        </div>
      )}
    </>
  );
}
